/**
 * Handbuch — vollständige Bedienungsanleitung (eigener Menüpunkt).
 * Statischer Inhalt als Markdown, gerendert über die geteilte Markdown-Komponente.
 * Für alle Rollen sichtbar; rollenspezifische Abschnitte sind gekennzeichnet.
 */

import { Markdown } from "@/components/Markdown"

const HANDBUCH = `
# Handbuch

Willkommen im **ninox-tester** — dem Feedback-Portal für eure Ninox-Anwendung.
Hier meldet ihr Fehler und Änderungswünsche mit Screenshot und
KI-geführtem Dialog. Aus jeder Meldung entsteht eine strukturierte Anforderung,
die netz-fabrik sichtet und in ein Entwicklungs-Ticket überführt.

---

## 1. Überblick

- **Melden:** Fehler oder Änderungswunsch erfassen — mit Screenshot der Ninox-Oberfläche, Markierungen und einem kurzen KI-Dialog.
- **Übersicht:** Alle eigenen (bzw. für die Organisation sichtbaren) Meldungen verfolgen, filtern und durchsuchen.
- **Chat:** Zu jeder Meldung direkt mit der Entwicklung schreiben.
- Die Ninox-Anwendung selbst wird **nicht** verändert — der ninox-tester liest nur die Struktur (read-only) und hilft beim Beschreiben.

## 2. Anmeldung & Rollen

Die Anmeldung erfolgt über euren zentralen RMNC-Login. Welche Funktionen ihr seht, hängt von eurer Rolle ab:

| Rolle | Darf … |
|---|---|
| **Kundennutzer** | Eigene Meldungen erfassen, absenden, im Chat schreiben, eigene Meldungen einsehen |
| **Kundenadmin** | Zusätzlich: alle Meldungen der Organisation sehen, Priorität ändern, Meldungen verwerfen |
| **Reviewer** (netz-fabrik) | Zusätzlich: Meldungen sichten, mit Ninox-Logik präzisieren, freigeben, Administration & Schema |
| **Eingeschränkter Reviewer** | Wie Reviewer, aber **ohne** Schema-Änderungshistorie und in der Administration **nur** den Verbrauch |

## 3. Eine Meldung erfassen (Menüpunkt „Melden")

1. **Ninox öffnen.** Über die Schaltfläche *In Ninox öffnen* startet ihr eure Ninox-Anwendung in einem **separaten Browser-Tab** (je nach Konfiguration *Live* oder *DEV*). Navigiert dort zu der Stelle, die ihr melden möchtet.
2. **Ninox-Tab verbinden.** Zurück im ninox-tester klickt ihr auf *Ninox-Tab verbinden* und gebt im Browser-Dialog den Ninox-Tab frei. Der verbundene Tab dient als Live-Vorschau.
3. **Screenshot aufnehmen.** Ein Klick auf die Vorschau erstellt einen Screenshot des aktuellen Ninox-Bildes.
4. **Markieren & schwärzen.** Markiert den relevanten Bereich mit Rechtecken/Pfeilen. Sensible Inhalte könnt ihr **schwärzen** — Schwärzungen werden **vor dem Hochladen** fest ins Bild eingebrannt (siehe Datenschutz).
5. **Kontext angeben.** Wählt die **betroffene Tabelle** aus oder fügt die **Adresszeile aus dem Ninox-Tab** ein. So weiß die KI, um welche Stelle der Datenbank es geht.
6. **KI-Dialog führen.** Beschreibt Fehler oder Wunsch in eigenen Worten. Die KI stellt Rückfragen und erstellt daraus ein **Mini-PRD** mit *Ist-Zustand*, *Soll-Zustand* und *Akzeptanzkriterien*.
7. **Prüfen & absenden.** Kontrolliert das Ergebnis und sendet die Meldung ab. Bis dahin ist sie ein **Entwurf** und nur für euch sichtbar; ihr könnt sie jederzeit verwerfen.

> **Tipp:** Ihr könnt mehrere Screenshots hinzufügen und die Meldung später weiterbearbeiten, solange sie ein Entwurf ist.

## 4. Übersicht — Meldungen verfolgen

Die **Übersicht** ist die Startseite und zeigt eure Meldungen als Karten.

### Status-Filter
- Standardmäßig werden **nur offene** Meldungen angezeigt (Neu, Gesichtet, Freigegeben, In Umsetzung).
- **Klick** auf einen Status-Chip nimmt diesen Status hinzu oder entfernt ihn.
- **Doppelklick** auf einen Chip zeigt **nur** diesen Status (alle anderen werden abgewählt).
- *Nur offene* setzt auf die Standardauswahl zurück.

### Datumsstrahl
- Über die Schaltfläche **Datumsstrahl** blendet ihr einen horizontal scrollbaren Tages-Streifen ein. Er springt beim Einschalten auf **heute**.
- Ein Klick auf einen Tag zeigt die Meldungen, die **an diesem Tag erstellt** wurden.
- Bei aktivem Datumsstrahl ist der Status-Filter deaktiviert (es werden alle Stati des Tages gezeigt).

### Schnellsuche
- Öffnet die Suche mit **⌘K** bzw. **Strg + K** (oder über *Suchen* in der Kopfleiste).
- Bei **mehreren Wörtern** müssen **alle** vorkommen (Und-Suche) — verteilt über die Felder und in beliebiger Reihenfolge.
- Durchsucht werden **alle** in der Übersicht sichtbaren Angaben: Titel, Beschreibungen, Akzeptanzkriterien, Melder-Name, Ninox-Stelle und die #Nummer (Meldung oder Issue).

### Entwurf löschen
- Auf einer **Entwurfs-Karte** könnt ihr direkt auf das **Papierkorb-Symbol** klicken und den Entwurf nach Rückfrage endgültig löschen.

## 5. Meldungs-Detail & Chat

Ein Klick auf eine Meldung öffnet die **Detailansicht** mit Ist/Soll, Akzeptanzkriterien, Screenshots, der Ninox-Stelle und dem Status-Verlauf.

Über den **Chat** sprecht ihr direkt mit der Entwicklung (netz-fabrik) — auch nachdem aus der Meldung bereits ein Entwicklungs-Ticket geworden ist. Neue, ungelesene Nachrichten werden in der Übersicht mit einem Zähler-Badge angezeigt.

## 6. Der Weg einer Meldung (Status)

| Status | Bedeutung |
|---|---|
| **Entwurf** | Noch nicht abgesendet, nur für euch sichtbar |
| **Neu** | Abgesendet, wartet auf Sichtung |
| **Gesichtet** | Von netz-fabrik geprüft/geschärft |
| **Freigegeben** | Als Entwicklungs-Ticket übernommen |
| **In Umsetzung** | Wird bearbeitet |
| **Erledigt** | Umgesetzt |
| **Abgelehnt / Verworfen** | Nicht weiterverfolgt (mit Begründung) |

## 7. Für Kundenadmins

- **Organisations-Sicht:** Über die Umschaltung *Meine Meldungen / Organisation* seht ihr alle Meldungen eurer Organisation.
- **Priorität** einer Meldung anpassen.
- Noch nicht bearbeitete Meldungen **verwerfen** (mit Begründung).

## 8. Für Reviewer (netz-fabrik)

Zusätzlich zu den obigen Funktionen:

- **Review** (Menüpunkt *Review*): Meldungen **sichten** und schärfen.
- **Mit Ninox-Logik präzisieren:** Ein Assistent lädt die Formeln und Trigger der betroffenen Tabelle, löst abhängige Formeln bis zur Quelle auf und kennt die Ninox-Formelsprache. Der Vorschlag lässt sich per *Übernehmen* in die Meldung schreiben.
- **Freigeben:** Erzeugt aus der Meldung ein **Forgejo-Issue** (Entwicklungs-Ticket).
- **Administration** (*Administration*): KI-Anbieter und -Dienste konfigurieren, **Verbrauch** einsehen, Service-API-Token verwalten.
- **Schema** (*Schema*): Struktur-Browser der Ninox-Datenbanken, Änderungs-Historie und Schema-Backup-Läufe.

> **Eingeschränkter Reviewer:** sieht im Schema **keine** Änderungs-Historie und in der Administration **nur** den Verbrauch — alle übrigen Reviewer-Funktionen bleiben.

## 9. Datenschutz & Sicherheit

- **Screenshots:** Schwärzungen werden bereits **vor dem Upload** fest ins Bild eingebrannt — das gespeicherte Bild enthält keine geschwärzten Inhalte mehr.
- **KI sieht nur Struktur:** Bei der Erfassung nutzt die KI ausschließlich die **Struktur** eurer Datenbank (Tabellen/Felder), **niemals Daten**. Die volle Logik (Formeln/Trigger) ist netz-fabrik-intern und nur für Reviewer sichtbar.
- **Read-only:** Der Zugriff auf Ninox erfolgt strikt lesend — es werden keine Datensätze verändert.

## 10. Tipps & Fehlerbehebung

- **Tab-Freigabe:** Beim *Ninox-Tab verbinden* im Browser-Dialog genau den Ninox-Tab auswählen und freigeben. Wird die Freigabe beendet, einfach neu verbinden.
- **Kein Screenshot möglich?** Die Tab-Freigabe funktioniert in aktuellen Chromium-basierten Browsern (Chrome/Edge) am zuverlässigsten.
- **Keine KI-Antwort (Fehler 503)?** Dann ist noch kein KI-Anbieter hinterlegt — das richtet netz-fabrik in der Administration ein.
- **Meldung nicht auffindbar?** Prüft den **Status-Filter** (Standard: nur offene) bzw. schaltet den **Datumsstrahl** aus.

---

*Fragen, die dieses Handbuch nicht beantwortet? Schreibt sie direkt im **Chat** einer Meldung — netz-fabrik hilft weiter.*
`

export function HandbuchPage() {
  return (
    <div className="mx-auto max-w-[820px]">
      <h1 className="mb-6 text-[26px] font-bold tracking-tight">Handbuch</h1>
      <div className="rounded-xl border bg-card p-6 shadow-card md:p-8">
        <Markdown inhalt={HANDBUCH} />
      </div>
    </div>
  )
}
